import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { ArrowIcon, Button, Logo, MicIcon, Orb, SplitText } from 'components';
import { STORES_PING_EVENT } from 'components/layout/Footer';
import { quote } from 'src/utils/format';
import { gsap, ScrollTrigger, useGSAP } from 'src/utils/gsap';
import { DESKTOP_QUERY } from 'src/utils/motion';
import { scrollToTarget } from 'src/utils/smoothScroll';

import { usePage } from '../../pages';

import style from './Agent.module.scss';

const STEP_COUNT = 6;
const AUTO_INTERVAL = 5400;
const ACTION_VARIANTS = [['primary', 'ghost'], ['primary'], ['primary'], ['ghost']];
const BARS = [70, 84, 78, 92, 83, 75];
const WAVE = Array.from({ length: 26 }, (_, i) => ({
  '--s': (0.35 + Math.abs(Math.sin(i * 1.7)) * 0.65).toFixed(2),
  animationDelay: `${(-(i * 0.073)).toFixed(2)}s`
}));

const Reply = ({ kicker, className, children }) => (
  <div className={cn(style.reply, className)}>
    <span className={style.k}>{kicker}</span>
    {children}
  </div>
);

const Agent = () => {
  const { t, i18n } = useTranslation('page');
  const { id } = usePage();
  const steps = t(`${id}.agent.steps`, { returnObjects: true });
  const chats = t(`${id}.agent.chats`, { returnObjects: true });

  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const barRefs = useRef([]);
  const pinTrigger = useRef(null);
  const visible = useRef(false);
  const spoken = useRef('0-0');

  const [step, setStep] = useState(0);
  const [replay, setReplay] = useState(0);
  const [auto, setAuto] = useState(false);
  const [autoKey, setAutoKey] = useState(0);
  const [speaking, setSpeaking] = useState(false);

  // The phone "speaks" for a moment after each new message
  useEffect(() => {
    const key = `${step}-${replay}`;
    if (spoken.current === key) return;
    spoken.current = key;

    setSpeaking(true);
    const id = setTimeout(() => setSpeaking(false), 1300);
    return () => clearTimeout(id);
  }, [step, replay]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => (visible.current = entry.isIntersecting), { threshold: 0.35 });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Mobile: switch topics automatically while the section is visible
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => visible.current && setStep((s) => (s + 1) % STEP_COUNT), AUTO_INTERVAL);
    return () => clearInterval(id);
  }, [auto, autoKey]);

  // Desktop: pin the section and let scroll drive the topics
  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add(`${DESKTOP_QUERY} and (prefers-reduced-motion: no-preference)`, () => {
        pinTrigger.current = ScrollTrigger.create({
          trigger: pinRef.current,
          start: 'top top',
          end: () => `+=${window.innerHeight * STEP_COUNT * 0.7}`,
          pin: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            const x = Math.min(STEP_COUNT - 0.001, self.progress * STEP_COUNT);
            const i = Math.floor(x);
            setStep(i);
            barRefs.current.forEach((bar, j) => (bar.style.transform = j === i ? `scaleX(${(x - i).toFixed(3)})` : ''));
          }
        });
        return () => {
          pinTrigger.current = null;
          barRefs.current.forEach((bar) => (bar.style.transform = ''));
        };
      });

      mm.add('(max-width: 959px) and (prefers-reduced-motion: no-preference)', () => {
        setAuto(true);
        return () => setAuto(false);
      });

      return () => mm.revert();
    },
    { scope: sectionRef }
  );

  const handleStepClick = (i) => {
    const pin = pinTrigger.current;
    if (pin) {
      scrollToTarget(pin.start + ((i + 0.5) / STEP_COUNT) * (pin.end - pin.start), { duration: 1.4 });
      return;
    }
    setStep(i);
    setReplay((r) => r + 1);
    setAutoKey((k) => k + 1);
  };

  const handleMicClick = () => {
    scrollToTarget('#pobierz', {
      offset: -window.innerHeight * 0.55,
      duration: 1.8,
      onComplete: () => window.dispatchEvent(new Event(STORES_PING_EVENT))
    });
  };

  const chat = chats[step];
  const userBubble = (className) => <div className={cn(style.user, className)}>{quote(chat.user, i18n.language)}</div>;

  return (
    <section
      id='agent'
      ref={sectionRef}
      className={cn('sec tone-dark', style.agent, auto && style.auto)}
      data-tone='dark'
    >
      <div
        className={style.pin}
        ref={pinRef}
      >
        <div
          className={style.glow}
          aria-hidden='true'
        />
        <div className={cn('wrap', style.grid)}>
          <div className={style.copy}>
            <p className='eyebrow violet'>
              <span className='dot' />
              {t(`${id}.agent.eyebrow`)}
            </p>
            <SplitText
              as='h2'
              className='h2'
              reveal
            >
              {t(`${id}.agent.title`)} <span className='serif-i accent-v'>{t(`${id}.agent.titleAccent`)}</span>
            </SplitText>
            <p className='lead'>{t(`${id}.agent.lead`)}</p>
            <ol className={style.steps}>
              {steps.map((item, i) => (
                <li key={i}>
                  <button
                    type='button'
                    className={cn(style.step, i === step && style.on)}
                    aria-current={i === step ? 'step' : 'false'}
                    onClick={() => handleStepClick(i)}
                  >
                    <span className={style.name}>{item.name}</span>
                    <ArrowIcon className={style.ico} />
                    <span className={style.q}>
                      <span>
                        <span>{quote(item.quote, i18n.language)}</span>
                      </span>
                    </span>
                    <i
                      className={style.bar}
                      ref={(el) => (barRefs.current[i] = el)}
                    />
                  </button>
                </li>
              ))}
            </ol>
            <p className={style.pull}>{t(`${id}.agent.pull`)}</p>
          </div>

          <div className={style.phoneCol}>
            <div className={cn(style.phone, speaking && style.speaking)}>
              <div className={style.screen}>
                <span
                  className={style.notch}
                  aria-hidden='true'
                />
                <div className={style.top}>
                  <Logo label='Avalio' />
                  <span className={style.pill}>
                    <i />
                    {t(`${id}.agent.pill`)}
                  </span>
                </div>
                <div className={style.orbBox}>
                  <Orb
                    className={style.orb}
                    count={650}
                    scale={0.3}
                    mode={speaking ? 'speak' : 'idle'}
                  />
                </div>
                <div
                  className={style.wave}
                  aria-hidden='true'
                >
                  {WAVE.map((bar, i) => (
                    <i
                      key={i}
                      style={bar}
                    />
                  ))}
                </div>
                <div
                  key={`${step}-${replay}`}
                  className={style.chat}
                  aria-live='polite'
                >
                  {chat.replyKicker ? (
                    <>
                      <Reply
                        className={style.a1}
                        kicker={chat.kicker}
                      >
                        <span>{chat.text}</span>
                      </Reply>
                      {userBubble(style.a2)}
                      <Reply
                        className={style.a3}
                        kicker={chat.replyKicker}
                      >
                        <span>{chat.reply}</span>
                      </Reply>
                    </>
                  ) : (
                    <>
                      {userBubble(style.a1)}
                      <div className={cn(style.typing, style.tp)}>
                        <i />
                        <i />
                        <i />
                      </div>
                      <Reply
                        className={style.a2}
                        kicker={chat.kicker}
                      >
                        {chat.text && (
                          <span>
                            {chat.amount && <b>{chat.amount}</b>}
                            {chat.text}
                          </span>
                        )}
                        {chat.items && (
                          <ul>
                            {chat.items.map(([name, value]) => (
                              <li key={name}>
                                {name}
                                <b>{value}</b>
                              </li>
                            ))}
                          </ul>
                        )}
                        {chat.amount && (
                          <div className={style.bars}>
                            {BARS.map((height, i) => (
                              <i
                                key={i}
                                style={{ '--h': `${height}%` }}
                              />
                            ))}
                          </div>
                        )}
                        {chat.actions && (
                          <div className={style.row}>
                            {chat.actions.map((action, i) => (
                              <Button
                                key={action}
                                size='sm'
                                variant={ACTION_VARIANTS[step][i]}
                                className={style.replyBtn}
                              >
                                {action}
                              </Button>
                            ))}
                          </div>
                        )}
                      </Reply>
                    </>
                  )}
                </div>
                <div className={style.mic}>
                  <button
                    type='button'
                    aria-label={t(`${id}.agent.micAria`)}
                    onClick={handleMicClick}
                  >
                    <MicIcon />
                  </button>
                  <span>{t(`${id}.agent.mic`)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Agent;
